import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import ResourceUploader from '../../Forms/ResourceUploader/ResourceUploader';
import ImageViertController from './ImageViertController';
import { getImageElement } from '../../../services/resources';

const Container = styled.div`
    flex: 1;
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: stretch;
    color: #fff;
`;

const ResourceList = styled.div`
    flex: 2;
    width: 40%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
    border-right: 1px solid #444;
`;

const ResourceView = styled.div`
    flex: 3;
    width: 60%;
    height: 100%;

    & canvas {
        width: 100%;
        height: 100%;
    }
`;

const ZoomBar = styled.div`
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 2;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
`;

const ZoomValue = styled.button`
    width: 50px;
    height: 30px;
    background-color: #000;
    font-size: 12px;
    color: #ccc;
    border: 1px solid #ccc;
    border-left: 0px;
    border-right: 0px;
`;

const BtnZoom = styled.button`
    width: 30px;
    height: 30px;
    background-color: #000;
    font-size: 14px;
    color: #ccc;
    border: 1px solid #ccc;
    cursor: pointer;
    transition: all .4s ease 0s;
    position: relative;

    &:last-child {
        border-left: 0px;
        border-top-right-radius: 15px;
        border-bottom-right-radius: 15px;
    }

    &:first-child {
        border-right: 0px;
        border-top-left-radius: 15px;
        border-bottom-left-radius: 15px;
    }

    &:hover {
        color: #000;
        background-color: #ccc;
    }

    :focus {
        outline: none;
    }

    :before {
        content: '${p => p.icon}';
        font-family: "Material Design Icons";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
    }
`;


export default ({onChange, value, showFileList, onRef}) => {

    const [filesSelected, setFilesSelected] = useState([]);
    const [imageData, setImageData] = useState('');
    const [scale, setScale] = useState(100);

    const resourceRef = useRef();
    const canvasRef = useRef();
    const imageInstance = useRef();

    useEffect(() => {
        if (typeof onRef === 'function') {
            onRef(resourceRef);
        }
    }, [onRef]);

    useEffect(() => {
        let ctx = canvasRef.current.getContext('2d');
        let frameId = null;

        imageInstance.current = new ImageViertController(ctx);
        
        imageInstance.current.resize();
        imageInstance.current.init();
        
        const render = () => {
            imageInstance.current.draw();
            frameId = window.requestAnimationFrame(render);
        }
        render();

        const mouseDown = (e) => {imageInstance.current.mouseDown(e)};
        const mouseUp = (e) => imageInstance.current.mouseUp(e);
        const mouseMove = (e) => imageInstance.current.mouseMove(e);
        const preventContextMenu = (e) => e.preventDefault();
        const scrollEventFn = (e) => imageInstance.current.manageScroll(e);

        window.addEventListener('mousemove', mouseMove);
        window.addEventListener('mouseup', mouseUp);
        canvasRef.current.addEventListener('mousedown', mouseDown);
        canvasRef.current.addEventListener('contextmenu', preventContextMenu);
        canvasRef.current.addEventListener('wheel', scrollEventFn);

        const cref = canvasRef.current;

        return () => {
            window.cancelAnimationFrame(frameId);
            cref.removeEventListener('mousedown', mouseDown);
            cref.removeEventListener('contextmenu', preventContextMenu);
            cref.removeEventListener('wheel', scrollEventFn);
            window.removeEventListener('mousemove', mouseMove);
            window.removeEventListener('mouseup', mouseUp);
            imageInstance.current.clear();
        }
    }, []);
    
    useEffect(() => {
        imageInstance.current.setData(imageData);
        imageInstance.current.resize();
        imageInstance.current.reset();
        setScale(100);
    }, [imageData]);

    useEffect(() => {
        if (!showFileList) {
            try {
                const file = JSON.parse(value);
                if (file && file[0] && file[0].id && file[0].name) {
                    setFilesSelected(file)
                } else {
                    setFilesSelected([])
                }
            } catch(e) {
                console.error(Object.assign(e,{component:'ImageViewer', id: 'parseImageViewer'}));
            }
        }
    }, [showFileList, value]);

    useEffect(() => {
        let shouldSetData = true;

        (async () => {
            if (!filesSelected[0] || !filesSelected[0].id) {
                if (shouldSetData) {
                    setImageData('');
                }
                return;
            }

            let data = await getImageElement(filesSelected[0].id, false);
            if (shouldSetData) {
                setImageData(data.object);
            }
        })();

        return () => {
            shouldSetData = false;
        };
    }, [filesSelected]);

    useEffect(() => {
        if (typeof onChange === 'function') {
            onChange(filesSelected);
        }
    }, [onChange, filesSelected])

    const zoomIn = () => {
        imageInstance.current.zoomIn();
        setScale(imageInstance.current.getCurrentZoom());
    }

    const zoomOut = () => {
        imageInstance.current.zoomOut();
        setScale(imageInstance.current.getCurrentZoom());
    }
    
    
    return <Container>
        <ZoomBar>
            <BtnZoom icon="\F06ED" onClick={() => {zoomIn()}}></BtnZoom>
            <ZoomValue>{scale}%</ZoomValue>
            <BtnZoom icon="\F06EC" onClick={() => {zoomOut()}}></BtnZoom>
        </ZoomBar>
        {showFileList ? <ResourceList>
            <ResourceUploader 
                defaultValue={value}
                ref={resourceRef}
                mode="single"
                hideTypeColumn={true}
                onSelected={setFilesSelected}
            />
        </ResourceList> : null}
        <ResourceView>
            <canvas width="1" height="1" ref={canvasRef}></canvas>
        </ResourceView>
    </Container>
}